import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Dispatch, RootState } from '../../../../store/store';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import IconEditOutlined from '../../../../components/Icons/IconEditOutlined/IconEditOutlined';
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import { ElementSize } from '../../../../types/element-size';
import ROUTE from '../../../../constants/route-constants';
import { extractChannelNumberFromId } from '../../../../utils/channel-utils';
import { CYCLE_MEASUREMENT_SYMBOL, EXTENDED_INTERVAL_UPDATE_DATA } from '../../../../constants/common';
import { ChannelState } from '../../../../enums/channel-state.enum';
import Tooltip from '../../../../components/Tooltip';
import EmptyDataTable from '../../../../components/EmptyDataTable/EmptyDataTable';
import { IChannelName } from '../../../../types/channel-types';

type ComponentProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

// Semantic state highlight colors — take priority over zebra when a channel is
// actively running, paused, or faulted.  Stopped/inactive rows fall back to zebra.
const ROW_STATE_BG_CLASS: Partial<Record<ChannelState, string>> = {
  [ChannelState.RUNNING]: 'bg-green-100',
  [ChannelState.PAUSED]: 'bg-orange-100',
  [ChannelState.FAULTED]: 'bg-rose-100',
};

const Channels: React.FC<ComponentProps> = ({
  getChannels,
  channels,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    getChannels(id);
    const intervalId = setInterval(() => getChannels(id), EXTENDED_INTERVAL_UPDATE_DATA);

    return () => clearInterval(intervalId);
  }, [id]);

  const onEditHandler = (channelId: string) => {
    const path = generatePath(
      ROUTE.EDIT_CONTROLLER_CHANNEL,
      { id, channel_number: extractChannelNumberFromId(channelId) }
    );
    navigate(path);
  };

  const goToActiveCycleScreen = (channel: IChannelName) => {
    if (!channel.activeCycleScreenId || !channel.activeCycleClientId || !channel.activeCycleContractId) {
      return;
    }

    const path = generatePath(ROUTE.SCREEN_CYCLES, {
      clientId: channel.activeCycleClientId,
      contractId: channel.activeCycleContractId,
      screenId: channel.activeCycleScreenId,
    });
    navigate(path);
  };

  // All channels on a controller share the same unit — read from the first one.
  const controllerUnits = channels[0]?.units;
  const controllerUnitsLabel = controllerUnits != null ? CYCLE_MEASUREMENT_SYMBOL[controllerUnits] : null;

  return (
    <div className='p-edit-controller-channels'>
      {!channels.length ? (
        <EmptyDataTable />
      ) : (
        <div className="c-table h-full overflow-x-auto">
          {controllerUnitsLabel && (
            <p className="mb-2 text-sm font-medium text-black dark:text-white">
              Controller units:&nbsp;<span className="font-bold">{controllerUnitsLabel}</span>
            </p>
          )}
          <table className="c-table__table w-full">
            <thead className="c-table__thead text-nowrap leading-6 text-white">
              <tr className="c-table__tr-head bg-primary">
                <th className="py-1 pl-5 text-left text-xs">Channel</th>
                <th className="py-1 text-left text-xs">Name</th>
                <th className="py-1 text-left text-xs">Temperature</th>
                <th className="py-1 text-left text-xs">Set Point</th>
                <th className="py-1 text-left text-xs">Cycle Name</th>
                <th className="w-[120px] py-1 text-center text-xs">Edit</th>
              </tr>
            </thead>
            <tbody className="c-table__tbody font-medium whitespace-nowrap">
              {channels.map((channel, idx) => {
                const zebraClass = idx % 2 === 0 ? 'bg-white' : 'bg-gray-2';
                const bgClass = ROW_STATE_BG_CLASS[channel.state] ?? zebraClass;

                return (
                  <tr
                    key={channel.id}
                    className={`shadow-inset-table-top hover:bg-stroke ${bgClass}`}
                  >
                    <td className="text-xs leading-6">
                      <p className="py-2 pl-5">{channel.channel}</p>
                    </td>
                    <td className="text-xs leading-6">
                      <p className="py-2">{channel.name ? channel.name : '<empty>'}</p>
                    </td>
                    <td className="text-xs leading-6">
                      <p className="py-2">{channel.temperature}</p>
                    </td>
                    <td className="text-xs leading-6">
                      <p className="py-2">{channel.setpoint ?? '-'}</p>
                    </td>
                    <td className="text-xs leading-6">
                      <p className="py-2">
                        {channel.activeCycleName ? (
                          <Tooltip
                            hint={channel.activeCycleLocationName ?? ''}
                            hintClassName="left-0 bottom-7"
                            isCustomSettings
                          >
                            {channel.activeCycleScreenId ? (
                              <span
                                className="cursor-pointer text-primary hover:underline"
                                onClick={() => goToActiveCycleScreen(channel)}
                              >
                                {channel.activeCycleName}
                              </span>
                            ) : (
                              <span>{channel.activeCycleName}</span>
                            )}
                          </Tooltip>
                        ) : (
                          '-'
                        )}
                      </p>
                    </td>
                    <td className="pr-5">
                      <div className="flex flex-row items-center justify-center gap-x-1">
                        <InteractiveButton
                          onClick={() => onEditHandler(channel.id)}
                          customType="min-h-fit min-w-fit max-h-fit max-w-fit border border-foggy-mist rounded active:border-primary cursor-pointer"
                          style={{ padding: '8px' }}
                        >
                          <IconEditOutlined
                            stroke="inherit"
                            iconClassName="text-inherit"
                            size={ElementSize.XXS}
                          />
                        </InteractiveButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  channels: state.channels.channels,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getChannels: dispatch.channels.getChannels,
});

export default connect(mapState, mapDispatch)(Channels);
